import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell } from 'lucide-react';
import {
    getNotifications,
    dismissNotification,
    INotification,
    markNotificationAsRead,
    markNotificationShown,
} from '../../services/api/notificationService';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import NotificationCard from './NotificationCard';
import NotificationPreviewDialog from './NotificationPreviewDialog';

interface UnreadNotificationsProps {
    /** Use for dark header (e.g. Navigation bar) */
    variant?: 'default' | 'darkHeader';
}

const UnreadNotifications: React.FC<UnreadNotificationsProps> = ({
    variant = 'default',
}) => {
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] =
        useState<INotification | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const sheetOpenRef = useRef(false);
    const detailOpenedFromSheetRef = useRef(false);

    const updateSheetOpen = useCallback((open: boolean) => {
        sheetOpenRef.current = open;
        setSheetOpen(open);
    }, []);

    const openDetail = useCallback((notification: INotification) => {
        detailOpenedFromSheetRef.current = sheetOpenRef.current;
        setSelectedNotification(notification);
        setDetailOpen(true);

        if (notification._id) {
            markNotificationShown(notification._id)
                .then((updatedNotification) => {
                    if (!updatedNotification) return;
                    setNotifications((prev) =>
                        prev.map((item) =>
                            item._id === updatedNotification._id
                                ? updatedNotification
                                : item
                        )
                    );
                    setSelectedNotification((current) =>
                        current?._id === updatedNotification._id
                            ? updatedNotification
                            : current
                    );
                })
                .catch((): undefined => undefined);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getNotifications();
            const list = data || [];
            setNotifications(list);
            // Auto-show first important notification
            const firstImportant = list.find(
                (n: INotification) => n.isImportant
            );
            if (firstImportant) {
                openDetail(firstImportant);
            }
        } catch {
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [openDetail]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const closeDetail = async (): Promise<void> => {
        const notificationId = selectedNotification?._id;
        if (notificationId) {
            await dismissNotification(notificationId).catch(
                (): undefined => undefined
            );
        }
        setSelectedNotification(null);
        setDetailOpen(false);
        updateSheetOpen(detailOpenedFromSheetRef.current);
        detailOpenedFromSheetRef.current = false;
    };

    const markSelectedAsRead = async (): Promise<void> => {
        const notificationId = selectedNotification?._id;
        if (notificationId) {
            await markNotificationAsRead(notificationId).catch(
                (): undefined => undefined
            );
            setNotifications((prev) =>
                prev.filter((n) => n._id !== notificationId)
            );
        }
        setSelectedNotification(null);
        setDetailOpen(false);
    };

    const unreadCount = notifications.length;

    return (
        <>
            <Sheet open={sheetOpen} onOpenChange={updateSheetOpen}>
                <SheetTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                            'relative',
                            variant === 'darkHeader' &&
                                'text-white hover:bg-white/10 hover:text-white'
                        )}
                        aria-label="Notifications"
                    >
                        <Bell className="h-5 w-5" />
                        {unreadCount > 0 && (
                            <Badge
                                variant="destructive"
                                className="absolute -right-1 -top-1 h-5 min-w-5 rounded-full px-1 text-xs"
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </Badge>
                        )}
                    </Button>
                </SheetTrigger>
                <SheetContent side="right" className={cn('w-full sm:max-w-md')}>
                    <SheetHeader>
                        <SheetTitle>Notifications</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 space-y-2">
                        {loading ? (
                            <p className="text-sm text-muted-foreground">
                                Loading…
                            </p>
                        ) : notifications.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No unread notifications
                            </p>
                        ) : (
                            notifications.map((n) => (
                                <NotificationCard
                                    key={n._id}
                                    variant="display"
                                    notification={n}
                                    onClick={(notification) => {
                                        openDetail(notification);
                                    }}
                                />
                            ))
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            <NotificationPreviewDialog
                notification={selectedNotification}
                open={detailOpen}
                onOpenChange={(open) => !open && closeDetail()}
                footerAction={
                    <Button onClick={markSelectedAsRead}>Mark as read</Button>
                }
            />
        </>
    );
};

export default UnreadNotifications;
